// Third Party Libraries Imports
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";
import * as XLSX from "xlsx";

// Local Imports
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";

class UploadCustomersService {
  private dispatch: any;

  constructor(dispatch?: any) {
    this.dispatch = dispatch;
  }

  private setLoading = (loading: boolean) => {
    if (this.dispatch) {
      this.dispatch({ type: LOADING, payload: loading });
    }
  };

  public async getOnboardingListData(): Promise<any[] | undefined> {
    this.setLoading(true);

    try {
      const response: AxiosResponse<{
        data: any[];
        message: string;
      }> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/onboarding`
      );
      return response.data.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/customers/upload`);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }

  public extractExcelDataAsJson(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Convert array format to object format if headers exist
          if (jsonData.length > 0) {
            const headers = jsonData[0] as string[];
            const rows = jsonData.slice(1);

            const formattedData = rows.map((row: any[], rowIndex: number) => {
              const obj: any = {};
              headers.forEach((header, index) => {
                // Keep original header names as they are
                obj[header] = row[index] || "";
              });

              // Add row number for debugging
              obj._rowIndex = rowIndex + 1;

              return obj;
            });

            resolve(formattedData);
          } else {
            console.warn("No data found in Excel file");
            resolve([]);
          }
        } catch (error) {
          console.error("Excel parsing error:", error);
          reject(new Error("Failed to parse Excel file"));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  public async uploadExcelData(data: any[]): Promise<boolean> {
    this.setLoading(true);

    try {
      // Validate data before sending
      if (!data || data.length === 0) {
        toast.error("No data to upload");
        return false;
      }

      // Prepare the data for upload - keep original format
      const preparedData = data?.map((item: any) => ({
        ...item,
        phoneNumber: item.phoneNumber?.toString()?.trim(),
        accountCodes: item.accountCodes
          ?.toString()
          ?.trim()
          ?.split(",")
          ?.map((code: string) => +code.trim()),
        // Remove debugging fields
        _rowIndex: undefined,
      }));

      const response: AxiosResponse<{
        message: string;
        success: boolean;
        error?: string;
      }> = await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/onboarding`,
        {
          accounts: preparedData,
        }
      );

      // Check if the response indicates success
      if (response.status === 201) {
        toast.success("Excel data uploaded successfully");
        return true;
      } else {
        const errorMessage =
          response.data.message || response.data.error || "Upload failed";
        toast.error(errorMessage);
        return false;
      }
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Upload failed";
        toast.error(errorMessage);
      }
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  public async getOnboardingFileData(id: string): Promise<any[] | undefined> {
    this.setLoading(true);

    try {
      const response: AxiosResponse<{
        data: any[];
        message: string;
      }> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/onboarding/${id}`
      );
      return response.data.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }

  public async getOnboardingFileDataById(
    id: string
  ): Promise<any[] | undefined> {
    this.setLoading(true);

    try {
      const response: any = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/onboarding/${id}`
      );
      return response?.data?.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }
}

export default UploadCustomersService;
